// Ubicación en el repo: src/pages/OftSales.jsx
// Listado de Ventas OFT (¿compró lentes o no?) — mismo patrón de
// tabla + búsqueda que Prescriptions.jsx/Exams.jsx: RLS ya restringe por
// patient_branch_match, esto solo acota a la sucursal activa del header.
// Sin fase de detalle/impresión — el prototipo tampoco la tiene para este
// módulo (ver DECISIONS.md).
import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { listOftsales, softDeleteOftsale } from '../lib/oftsales';
import { isModuleEnabledForBranch } from '../lib/branches';
import { formatPatientName, formatDate, formatCurrency } from '../lib/format';
import OftSaleFormModal from '../components/OftSaleFormModal';
import Spinner from '../components/Spinner';

export default function OftSales() {
  const { activeBranchId, activeBranch, professional } = useOutletContext();

  const [search, setSearch] = useState('');
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const refresh = useCallback(async () => {
    if (!activeBranchId) return;
    setLoading(true);
    setError('');
    try {
      setSales(await listOftsales({ branchId: activeBranchId, search }));
    } catch (err) {
      setError(err.message || 'No se pudo cargar el listado de ventas.');
    } finally {
      setLoading(false);
    }
  }, [activeBranchId, search]);

  useEffect(() => {
    const t = setTimeout(refresh, 300);
    return () => clearTimeout(t);
  }, [refresh]);

  function handleNew() {
    setEditTarget(null);
    setFormOpen(true);
  }

  function handleEditClick(sale) {
    setEditTarget(sale);
    setFormOpen(true);
  }

  async function handleDeleteClick(sale) {
    const ok = window.confirm(`¿Eliminar este registro de venta de ${formatPatientName(sale.patients)}?`);
    if (!ok) return;
    try {
      await softDeleteOftsale(sale.id, professional.id);
      await refresh();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el registro.');
    }
  }

  function handleFormSaved() {
    setFormOpen(false);
    setEditTarget(null);
    refresh();
  }

  // Guard de UX (DECISIONS.md #42) — ver mismo comentario en Exams.jsx.
  if (activeBranch && !isModuleEnabledForBranch(activeBranch, 'OFT')) {
    return (
      <div className="patients-page">
        <h1>Ventas</h1>
        <p className="text-muted">El módulo OFT está deshabilitado para la sucursal activa ({activeBranch.name}).</p>
      </div>
    );
  }

  return (
    <div className="patients-page">
      <div className="patients-page__toolbar">
        <h1>Ventas</h1>
        <div className="patients-page__toolbar-right">
          <input
            type="text"
            className="search-input"
            placeholder="Buscar por paciente o RUT…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="button" className="button-primary" onClick={handleNew}>
            + Nueva venta
          </button>
        </div>
      </div>

      {error && <p className="auth-error">{error}</p>}

      <table className="patients-table">
        <thead>
          <tr>
            <th>Paciente</th>
            <th>Fecha</th>
            <th>Estado</th>
            <th>Precio</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={5}><Spinner inline /></td>
            </tr>
          ) : sales.length === 0 ? (
            <tr>
              <td colSpan={5}>Sin registros de venta.</td>
            </tr>
          ) : (
            sales.map((sale) => (
              <tr key={sale.id}>
                <td>
                  <div>{formatPatientName(sale.patients)}</div>
                  <div className="text-muted">{sale.patients?.rut || 'Sin RUT'}</div>
                </td>
                <td>{formatDate(sale.date)}</td>
                <td>
                  <span className={`badge ${sale.compro ? 'badge-ok' : 'badge-neutral'}`}>
                    {sale.compro ? 'Compró' : 'No compró'}
                  </span>
                </td>
                <td>{sale.compro ? formatCurrency(sale.price) : '—'}</td>
                <td className="actions-cell">
                  <button type="button" className="link-button" onClick={() => handleEditClick(sale)}>
                    Editar
                  </button>
                  <button type="button" className="link-button link-button--danger" onClick={() => handleDeleteClick(sale)}>
                    Eliminar
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {formOpen && (
        <OftSaleFormModal
          sale={editTarget}
          actorId={professional.id}
          onClose={() => {
            setFormOpen(false);
            setEditTarget(null);
          }}
          onSaved={handleFormSaved}
        />
      )}
    </div>
  );
}
